/**
 * First-Visit Consent Modal
 *
 * Pops up on the very first page load if the user hasn't yet accepted.
 * Blocks all interaction until they either:
 *   - check the consent box and click "I Agree — Continue", OR
 *   - click "Decline & Leave Site" (redirects to about:blank)
 *
 * Legally this is the strongest possible "clickwrap" consent pattern.
 * Acceptance is recorded with timestamp + locale in localStorage,
 * and a server-side audit record is created at signup (auth.py consent_log).
 *
 * Versioned via STORAGE_KEY so future Terms updates can force re-consent.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ConsentModal.css';

const STORAGE_KEY = 'sb_consent_v1';

export const ConsentModal = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem(STORAGE_KEY);
    if (!accepted) setOpen(true);
  }, []);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  if (!open) return null;

  const accept = () => {
    const record = {
      accepted: true,
      version: '2026-04-01',
      locale: i18n.language || 'en',
      accepted_at: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setOpen(false);
  };

  const decline = () => {
    // Send them away from the site
    window.location.href = 'about:blank';
  };

  return (
    <div className="consent-backdrop" role="dialog" aria-modal="true" aria-labelledby="consent-title" data-testid="consent-modal">
      <div className="consent-modal">
        <h2 id="consent-title" className="consent-title">{t('consent_modal.title')}</h2>
        <p className="consent-intro">{t('consent_modal.intro')}</p>

        <ul className="consent-points">
          <li><strong>1.</strong> {t('consent_modal.p_as_is')}</li>
          <li><strong>2.</strong> {t('consent_modal.p_not_advice')}</li>
          <li><strong>3.</strong> {t('consent_modal.p_arbitration')}</li>
          <li><strong>4.</strong> {t('consent_modal.p_liability_cap')}</li>
          <li><strong>5.</strong> {t('consent_modal.p_age')}</li>
        </ul>

        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="consent-terms-link"
          data-testid="consent-view-terms"
        >
          {t('consent_modal.view_terms')} →
        </a>

        <label className="consent-checkbox">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid="consent-checkbox"
          />
          <span>{t('consent_modal.checkbox')}</span>
        </label>

        <div className="consent-actions">
          <button
            className="consent-decline"
            onClick={decline}
            data-testid="consent-decline-btn"
          >
            {t('consent_modal.decline')}
          </button>
          <button
            className="consent-accept"
            onClick={accept}
            disabled={!checked}
            data-testid="consent-accept-btn"
          >
            {t('consent_modal.accept')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentModal;
